import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { trackPageView, setTrackContext } from "./utils/track";
import { installInteractionTracking } from "./utils/interactions";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ChangePassword from "./pages/ChangePassword";
import SignUp from "./pages/SignUp";
import VerifyPhone from "./pages/VerifyPhone";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import ScannersList from "./pages/ScannersList";
import ScannerDetail from "./pages/ScannerDetail";
import Settings from "./pages/Settings";
import TokensPage from "./pages/TokensPage";
import PaidRoute from "./components/PaidRoute";
import PricingPage from "./pages/PricingPage";
import UploadProcessor from "./pages/UploadProcessor";
import TicketsList from "./pages/TicketsList";
import TicketDetail from "./pages/TicketDetail";
import NewTicket from "./pages/NewTicket";
import ApiPage from "./pages/ApiPage";
import ReferralsPage from "./pages/ReferralsPage";
import Sidebar from "./components/Sidebar";
// Clics, copias e impresiones (utils/interactions.js): una vez por carga.
installInteractionTracking();

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Cargando...</p>
      </div>
    </div>
  );
}

function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.force_password_change) return <Navigate to="/login" replace />;
  // If user has completed onboarding, redirect to dashboard
  if (user.onboarding_completed) return <Navigate to="/" replace />;
  return children;
}

function DashboardRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.force_password_change) return <Navigate to="/login" replace />;
  // Antes: `if (user.phone_required) → /settings`. Desde que WhatsApp es
  // opcional (23-9-2026) toda cuenta sin teléfono lo tiene, y como /settings
  // también pasa por aquí, la redirección entraba en bucle y la app salía en
  // blanco a todo el que se registraba solo con la web. Quitado el 24-9-2026:
  // la invitación a añadir WhatsApp está en cada plantilla (ScannerDetail).
  // Si no ha completado el onboarding, redirigir — excepto si venimos del propio onboarding.
  // El contexto local se actualiza 100ms después de la navegación; sin el flag, DashboardRoute
  // vería onboarding_completed=false y redirigiría a /onboarding antes de que el estado se actualice.
  if (!user.onboarding_completed && !location.state?.fromOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  // Desktop sidebar is always visible; mobile behavior is handled inside Sidebar.
  const saasNumber = process.env.REACT_APP_SAAS_NUMBER;

  return (
    <div className="flex">
      <Sidebar saasNumber={saasNumber} />

      <div className="flex-1 min-w-0 min-h-screen pt-14 lg:pt-0 lg:ml-72 bg-gray-50">{children}</div>
    </div>
  );
}

function App() {
  const { loading, sessionExpired, clearSessionExpired, user } = useAuth();
  const { i18n } = useTranslation();

  // Apply company language when user logs in
  useEffect(() => {
    if (user?.language) {
      // Only change language if it's a valid supported language
      const supportedLanguages = ["es", "en", "it", "pt", "de", "fr"];
      if (supportedLanguages.includes(user.language)) {
        i18n.changeLanguage(user.language);
      }
    }
  }, [user?.language, i18n]);

  const location = useLocation();

  // Pixel propio (utils/track.js): quién es (plan, idioma) y por dónde navega.
  useEffect(() => {
    setTrackContext({
      plan: user?.subscription_plan || undefined,
      lang: user?.language || i18n.language,
    });
  }, [user?.subscription_plan, user?.language, i18n.language]);

  useEffect(() => {
    if (loading) return;
    trackPageView(location.pathname);
  }, [location.pathname, loading]);

  // Show alert when session expires
  useEffect(() => {
    if (sessionExpired) {
      alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify-phone" element={<VerifyPhone />} />
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <Onboarding />
            </OnboardingRoute>
          }
        />
        <Route
          path="/"
          element={
            <DashboardRoute>
              <Dashboard />
            </DashboardRoute>
          }
        />
        <Route
          path="/scanners"
          element={
            <DashboardRoute>
              <ScannersList />
            </DashboardRoute>
          }
        />
        <Route
          path="/tickets"
          element={
            <DashboardRoute>
              <TicketsList />
            </DashboardRoute>
          }
        />
        <Route
          path="/tickets/:ticketId"
          element={
            <DashboardRoute>
              <TicketDetail />
            </DashboardRoute>
          }
        />
        <Route
          path="/new-ticket"
          element={
            <DashboardRoute>
              <NewTicket />
            </DashboardRoute>
          }
        />
        <Route
          path="/scanners/:id"
          element={
            <DashboardRoute>
              <ScannerDetail />
            </DashboardRoute>
          }
        />
        <Route
          path="/upload"
          element={
            <DashboardRoute>
              <UploadProcessor />
            </DashboardRoute>
          }
        />
        <Route
          path="/tokens"
          element={
            <DashboardRoute>
              <PaidRoute>
                <TokensPage />
              </PaidRoute>
            </DashboardRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <DashboardRoute>
              <PricingPage />
            </DashboardRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <DashboardRoute>
              <Settings />
            </DashboardRoute>
          }
        />
        <Route
          path="/api"
          element={
            <DashboardRoute>
              <ApiPage />
            </DashboardRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <DashboardRoute>
              <ReferralsPage />
            </DashboardRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
